import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { fetchSystemReportById } from '../services/api';
import { SystemInfo } from '../types';
import '../styles/ReportDetails.css';

const ReportJsonDetails: React.FC = () => {
    const { serialNumber, reportId } = useParams<{ serialNumber: string; reportId: string }>();
    const [report, setReport] = React.useState<SystemInfo | null>(null);
    const [loading, setLoading] = React.useState<boolean>(true);
    const [error, setError] = React.useState<string | null>(null);
    const [expandedSections, setExpandedSections] = React.useState<Set<string>>(new Set(['general', 'hardware', 'software', 'content']));

    React.useEffect(() => {
        const getReport = async () => {
            try {
                if (!reportId) {
                    setError('Missing report id');
                    return;
                }

                const data = await fetchSystemReportById(reportId);
                setReport(data);
            } catch (err) {
                const message = err instanceof Error ? err.message : 'Failed to fetch report json';
                setError(message);
            } finally {
                setLoading(false);
            }
        };

        getReport();
    }, [reportId]);

    const toggleSection = (section: string) => {
        const newExpanded = new Set(expandedSections);
        if (newExpanded.has(section)) {
            newExpanded.delete(section);
        } else {
            newExpanded.add(section);
        }
        setExpandedSections(newExpanded);
    };

    const renderField = (label: string, value: any) => {
        if (value === null || value === undefined || value === '') {
            return (
                <div key={label} className="field nullable">
                    <span className="field-label">{label}:</span>
                    <span className="field-value">-</span>
                </div>
            );
        }
        if (Array.isArray(value)) {
            return (
                <div key={label} className="field">
                    <span className="field-label">{label}:</span>
                    <span className="field-value">{value.join(', ')}</span>
                </div>
            );
        }
        return (
            <div key={label} className="field">
                <span className="field-label">{label}:</span>
                <span className="field-value">{String(value)}</span>
            </div>
        );
    };

    const Section: React.FC<{ title: string; id: string; children: React.ReactNode }> = ({ title, id, children }) => (
        <div className="section">
            <div className="section-header" onClick={() => toggleSection(id)}>
                <span className="section-toggle">{expandedSections.has(id) ? '▼' : '▶'}</span>
                <h3>{title}</h3>
            </div>
            {expandedSections.has(id) && <div className="section-content">{children}</div>}
        </div>
    );

    if (loading) {
        return <div className="loading">Loading report JSON...</div>;
    }

    if (error) {
        return <div className="error">{error}</div>;
    }

    if (!report) {
        return <div className="error">No report found.</div>;
    }

    return (
        <div className="dashboard">
            <h2>System Report Details</h2>
            <p className="summary">
                <Link to={`/report/${encodeURIComponent(serialNumber || '')}`}>← Back to records</Link>
            </p>

            <div className="report-sections">
                <Section title="General" id="general">
                    {renderField('Serial Number', report.serialNumber)}
                    {renderField('Customer', report.customer)}
                    {renderField('Hostname', report.hostName)}
                    {renderField('MAC Address', report.macAddress)}
                    {renderField('TeamViewer ID', report.teamViewerId)}
                    {renderField('Timestamp', new Date(report.timestamp).toLocaleString())}
                </Section>

                <Section title="Hardware" id="hardware">
                    {renderField('Processor', report.processor)}
                    {renderField('Motherboard', report.motherboard)}
                    {renderField('RAM', `${report.ramGB} GB`)}
                    {renderField('Free Disk Space', `${report.diskFreeGB} GB`)}
                    {renderField('GPU', report.gpu)}
                    {renderField('GPU Driver', report.gpuDriver)}
                    {renderField('Screens', report.screenCount)}
                    {renderField('Screen Resolutions', report.screenResolutions)}
                    {renderField('Screen Scaling', report.screenScaling?.map((s: number) => `${s}%`))}
                </Section>

                <Section title="Software" id="software">
                    {renderField('OS', report.osName)}
                    {renderField('Edition', report.edition)}
                    {renderField('Version', report.version)}
                    {renderField('Build', report.build)}
                    {renderField('Hauptwerk Version', report.hauptwerkVersion)}
                    {renderField('Hauptwerk Path', report.hauptwerkPath)}
                    {renderField('Hauptwerk Sample Sets', report.hauptwerkSamplesets)}
                    {renderField('Sweelinq Version', report.sweelinqVersion)}
                    {renderField('SystemReporter Version', report.systemReporterVersion)}
                </Section>

                <Section title="Content" id="content">
                    {renderField('Organ', report.organ)}
                    {renderField('Content Version', report.contentVersion)}
                    {renderField('Audio Channels', report.channels)}
                    {renderField('Setzer Size', report.setzer)}
                    {renderField('MIDI Controller', report.midiController?.toUpperCase())}
                    {renderField('HW Audio Device', report.audioDevice)}
                    {renderField('Audio Interface', report.audioInterface)}
                    {renderField('Default Playback', report.defaultPlayback)}
                    {renderField('VPO', report.vpo)}
                </Section>
            </div>
        </div>
    );
};

export default ReportJsonDetails;
